import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import NotificationListItem from '../../notifications/components/NotificationListItem';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { adminNotificationsActions, initialAdminNotificationsState } from '../../notifications/slice';
import { AdminNotification } from '../../../types';

export default function AdminNotificationsBell() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { items, unreadCount, listLoading } = useAppSelector(
    (s) => s.adminNotifications ?? initialAdminNotificationsState,
  );
  const token = useAppSelector((s) => s.adminAuth?.token);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (token) {
      dispatch(adminNotificationsActions.pollRequest());
    }
  }, [dispatch, token]);

  if (!token) return null;

  const open = Boolean(anchorEl);
  const previewItems = items.slice(0, 8);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    dispatch(adminNotificationsActions.fetchListRequest({ limit: 20 }));
  };

  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = (notification: AdminNotification) => {
    if (!notification.read) {
      dispatch(adminNotificationsActions.markReadRequest(notification.id));
    }
    handleClose();
    navigate(notification.link);
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        size="small"
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsNoneRoundedIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: 'calc(100vw - 24px)' } } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={() => dispatch(adminNotificationsActions.markAllReadRequest())}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />
        {listLoading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No notifications yet.
          </Typography>
        ) : (
          <List disablePadding sx={{ maxHeight: 400, overflow: 'auto' }}>
            {previewItems.map((n) => (
              <NotificationListItem
                key={n.id}
                notification={n}
                onOpen={handleNotificationClick}
                compact
              />
            ))}
          </List>
        )}
        <Divider />
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="small"
            onClick={() => {
              handleClose();
              navigate('/admin/notifications');
            }}
          >
            View all
          </Button>
        </Box>
      </Popover>
    </>
  );
}
